import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound } from "lucide-react";

export const CompanyUsers = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [openPassword, setOpenPassword] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "operator" });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/company/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/company/users", newUser);
      toast.success(`Usuário "${newUser.name}" criado`);
      setOpenCreate(false);
      setNewUser({ name: "", email: "", password: "", role: "operator" });
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Remover ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuário removido");
      fetchUsers();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (!openPassword) return;
    try {
      await api.patch(`/users/${openPassword.id}`, { password: newPassword });
      toast.success("Senha atualizada");
      setOpenPassword(null);
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="border border-border p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-2">
            Usuários da Empresa
          </div>
          <div className="font-heading text-2xl font-bold tracking-tight">
            {users.length} {users.length === 1 ? "usuário" : "usuários"}
          </div>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button className="rounded-none" data-testid="admin-add-user-btn">
              <Plus className="w-4 h-4 mr-2" /> Novo Usuário
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-lg" data-testid="admin-new-user-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-black tracking-tighter">Novo Usuário</DialogTitle>
            </DialogHeader>
            <form onSubmit={createUser} className="space-y-4 py-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em]">Nome</Label>
                <Input
                  value={newUser.name}
                  onChange={(e) => setNewUser((p) => ({ ...p, name: e.target.value }))}
                  className="mt-2 h-11 rounded-none"
                  data-testid="admin-user-name-input"
                  required
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em]">Email</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))}
                  className="mt-2 h-11 rounded-none"
                  data-testid="admin-user-email-input"
                  required
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em]">Senha Inicial</Label>
                <Input
                  type="text"
                  value={newUser.password}
                  onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))}
                  className="mt-2 h-11 rounded-none font-mono"
                  minLength={6}
                  data-testid="admin-user-password-input"
                  required
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em]">Função</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser((p) => ({ ...p, role: v }))}>
                  <SelectTrigger className="mt-2 h-11 rounded-none" data-testid="admin-user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operator">Operador (apenas chamadas)</SelectItem>
                    <SelectItem value="owner">Dono (acesso total)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" className="rounded-none uppercase tracking-[0.2em] w-full h-12">
                  Criar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-foreground/50 text-sm">Carregando…</div>
      ) : (
        <div className="border border-border">
          {users.map((u) => (
            <div
              key={u.id}
              className="flex items-center justify-between gap-4 px-4 py-3 border-b border-border last:border-b-0"
              data-testid={`admin-user-row-${u.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate flex items-center gap-2">
                  {u.name}
                  {u.id === user?.id && <span className="text-xs uppercase tracking-wider text-foreground/40">(você)</span>}
                </div>
                <div className="text-xs text-foreground/60 truncate">
                  {u.email} · <span className="uppercase tracking-wider">{u.role}</span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="rounded-none"
                onClick={() => {
                  setOpenPassword(u);
                  setNewPassword("");
                }}
                data-testid={`admin-change-password-${u.id}`}
              >
                <KeyRound className="w-4 h-4" />
              </Button>
              {u.id !== user?.id && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-none"
                  onClick={() => deleteUser(u)}
                  data-testid={`admin-delete-user-${u.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!openPassword} onOpenChange={(o) => !o && setOpenPassword(null)}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-black tracking-tighter">
              Alterar senha — {openPassword?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={changePassword} className="space-y-4 py-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em]">Nova Senha</Label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-2 h-11 rounded-none font-mono"
                minLength={6}
                data-testid="admin-new-password-input"
                required
              />
            </div>
            <DialogFooter>
              <Button type="submit" className="rounded-none uppercase tracking-[0.2em] w-full h-12">
                Atualizar Senha
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
